/**
 * FeatureExplainer — Collapsible inline banner with 3-part explanation:
 *   What: What is this feature?
 *   What we do: What will the system actually do?
 *   Results: How to interpret the results?
 *
 * Dismissable per feature (localStorage). Reinstated by "Explain Mode" in Settings.
 *
 * Visual pattern follows 21st.dev Collapsible Banner (ddoemonn): card surface,
 * muted icon pill, high-contrast body copy — not brand-gold on gold wash.
 */
import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, ChevronRight, X } from 'lucide-react';
import { clsx } from 'clsx';

interface FeatureExplainerProps {
    featureKey: string;    // localStorage key, e.g. 'betweenness-centrality'
    title: string;
    what: string;
    howItWorks: string;
    interpret: string;
    icon?: React.ReactNode;
    className?: string;
}

const STORAGE_PREFIX = 'explainer-dismissed-';

export const FeatureExplainer: React.FC<FeatureExplainerProps> = ({
    featureKey,
    title,
    what,
    howItWorks,
    interpret,
    icon,
    className,
}) => {
    const storageKey = `${STORAGE_PREFIX}${featureKey}`;

    const [isDismissed, setIsDismissed] = useState(() => {
        const globalMode = localStorage.getItem('explainerMode');
        if (globalMode === 'off') return true;
        return localStorage.getItem(storageKey) === 'true';
    });

    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === 'explainerMode') {
                if (e.newValue === 'detailed') {
                    setIsDismissed(false);
                    localStorage.removeItem(storageKey);
                } else if (e.newValue === 'off') {
                    setIsDismissed(true);
                }
            }
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, [storageKey]);

    const handleDismiss = () => {
        setIsDismissed(true);
        localStorage.setItem(storageKey, 'true');
    };

    if (isDismissed) return null;

    return (
        <div
            role="region"
            aria-labelledby={`explainer-${featureKey}-title`}
            className={clsx(
                'rounded-lg border border-border bg-card text-xs shadow-sm',
                className,
            )}
        >
            <div className="flex items-center gap-2 p-2.5">
                <span
                    aria-hidden="true"
                    className="grid size-[26px] shrink-0 place-items-center rounded-md bg-muted text-muted-foreground"
                >
                    {icon || <Info className="w-3.5 h-3.5" />}
                </span>

                <button
                    id={`explainer-${featureKey}-title`}
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                    className="group flex min-w-0 flex-1 items-center gap-1.5 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    type="button"
                >
                    {isExpanded
                        ? <ChevronDown className="w-3 h-3 shrink-0 text-muted-foreground group-hover:text-foreground" />
                        : <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground group-hover:text-foreground" />}
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-5 text-foreground">
                        {title}
                    </span>
                </button>

                <button
                    onClick={handleDismiss}
                    className="grid size-[26px] shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    title="Don't show again"
                    aria-label="Dismiss explanation"
                    type="button"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>

            {isExpanded && (
                <div className="space-y-2 border-t border-border/60 px-3 pb-3 pt-2.5 pl-[46px]">
                    <ExplainerRow label="What" text={what} />
                    <ExplainerRow label="What we do" text={howItWorks} />
                    <ExplainerRow label="Results" text={interpret} />
                </div>
            )}
        </div>
    );
};

const ExplainerRow: React.FC<{ label: string; text: string }> = ({ label, text }) => (
    <p className="text-[12.5px] leading-relaxed text-muted-foreground">
        <span className="font-semibold text-foreground">{label}: </span>
        {text}
    </p>
);
